import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { listenToShortstayReservaionData } from "../../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import axios from "axios";

const HostConfirmCancelShortstay: React.FC = () => {
  const { hostId, guestId, listingId, reservationId } = useParams<{
    hostId: string;
    guestId: string;
    listingId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const listing = useSelector((state: RootState) => state.shortstays.currentListing);
  const reservation = useSelector(
    (state: RootState) => state.shortstayReservations.reservationById
  );
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);

  const [showModal, setShowModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Fetch required data
  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
    if (reservationId) dispatch(listenToShortstayReservaionData(reservationId));
    if (allUsers.length === 0) dispatch(fetchAllUsers());
  }, [dispatch, listingId, reservationId, allUsers.length]);

  const guest = allUsers.find((u) => u.userId === guestId);

  const handleCancel = () => setShowModal(true);

  // ✅ Unified cancellation logic
  const confirmCancel = async () => {
    if (!reservation || !listing || !hostId || !guestId) {
      alert("Missing booking, listing, or user information.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`https://areluxe.com/applyShortRefund`, {
        reservationId: reservation.id,
        hostId,
        guestId,
        listingId,
        dateKey: reservation.checkIn,
        cancellationType: "host",
        cancellationReason,
      });

      navigate(`/shortdashboard/${hostId}`);
    } catch (error: any) {
      console.error("❌ Host cancel error:", error);
      alert(error?.response?.data?.message || "Failed to cancel booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Cancel Guest’s Booking</h2>

      {/* ✅ Split Header Section — Listing & Guest */}
      <div style={styles.headerSplit}>
        <div style={styles.leftHalf}>
          {listing?.photos?.[0] ? (
            <img
              src={listing.photos[0]}
              alt="Listing"
              style={styles.previewPhoto}
            />
          ) : (
            <div style={styles.placeholder}>No Image</div>
          )}
        </div>

        <div style={styles.rightHalf}>
          {guest ? (
            <>
              <img
                src={guest.profilePicture || "/placeholder.jpg"}
                alt="Guest"
                style={styles.avatarLarge}
                onClick={() => navigate(`/aboutme/${guest.userId}`)}
              />
              <h3 style={styles.guestName}>
                {guest.firstName} {guest.lastName}
              </h3>
              <p style={styles.guestLabel}>Your Guest</p>
            </>
          ) : (
            <p>No guest found</p>
          )}
        </div>
      </div>

      {/* ✅ Reservation Info */}
      <div style={styles.container}>
        <p><strong>Listing:</strong> {listing?.title || "Unnamed Listing"}</p>
        <p><strong>Check-in:</strong> {reservation?.checkIn}</p>
        <p><strong>Check-out:</strong> {reservation?.checkOut}</p>
        <p><strong>Total Nights:</strong> {reservation?.reservedDates}</p>

        <button onClick={handleCancel} style={styles.cancelButton}>
          Cancel Reservation
        </button>
      </div>

      {/* ✅ Modal Confirmation */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
        <h3>Confirm Cancellation</h3>

<p style={styles.warningText}>
  ⚠️ You will be <strong>penalised twice</strong> your listing’s daily rate — one day’s payout will be
  <strong>deducted as a penalty</strong> and another paid to the guest as <strong>compensation</strong>.
</p>
<p style={styles.noticeText}>
  This will refund the guest and apply a penalty to your payout.
</p>

<textarea
  style={styles.textarea}
  placeholder="Optional: reason for cancelling"
  value={cancellationReason}
  onChange={(e) => setCancellationReason(e.target.value)}
/>


            <div style={styles.modalActions}>
              <button
                onClick={confirmCancel}
                style={styles.confirmButton}
                disabled={loading}
              >
                {loading ? "Processing…" : "Yes, Cancel"}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={styles.cancelModalButton}
                disabled={loading}
              >
                No, Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostConfirmCancelShortstay;

/* ---------- STYLES ---------- */
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "80px 20px",
    color: "#fff",
    fontFamily: "Poppins, sans-serif",
  },
  title: {
    textAlign: "center",
    fontSize: "1.8rem",
    marginBottom: 20,
  },
  headerSplit: {
    display: "flex",
    width: "100%",
    maxWidth: 540,
    margin: "0 auto 20px",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #bf9b30",
  },
  leftHalf: {
    flex: 1,
    height: 200,
    background: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rightHalf: {
    flex: 1,
    background: "#111",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 10,
  },
  previewPhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  placeholder: {
    width: "100%",
    height: "100%",
    color: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#222",
    fontSize: "0.9rem",
  },
  avatarLarge: {
    width: 70,
    height: 70,
    borderRadius: "50%",
    border: "2px solid #bf9b30",
    objectFit: "cover",
    cursor: "pointer",
  },
  guestName: {
    color: "#bf9b30",
    fontWeight: 600,
    fontSize: "1rem",
  },
  guestLabel: {
    color: "#aaa",
    fontSize: "0.85rem",
  },
  container: {
    background: "#111",
    borderRadius: 16,
    padding: 24,
    maxWidth: 540,
    margin: "0 auto",
    border: "1px solid #bf9b30",
  },
  cancelButton: {
    marginTop: 20,
    padding: "12px 20px",
    borderRadius: 30,
    fontWeight: 600,
    border: "none",
    fontSize: "1rem",
    background: "#ff4444",
    color: "#fff",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    background: "#222",
    padding: 30,
    borderRadius: 16,
    textAlign: "center",
    color: "#fff",
    maxWidth: 400,
    width: "90%",
  },
  modalActions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "space-around",
  },
  confirmButton: {
    padding: "10px 20px",
    borderRadius: 20,
    border: "none",
    background: "#bf9b30",
    color: "#000",
    cursor: "pointer",
    fontWeight: 600,
  },
  cancelModalButton: {
    padding: "10px 20px",
    borderRadius: 20,
    border: "none",
    background: "#555",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
  textarea: {
    width: "100%",
    marginTop: 10,
    padding: 10,
    borderRadius: 8,
    minHeight: 80,
    border: "1px solid #ccc",
    resize: "vertical",
  },
  warningText: {
  background: "rgba(255, 68, 68, 0.1)",
  border: "1px solid rgba(255, 68, 68, 0.4)",
  borderRadius: 12,
  padding: "10px 14px",
  color: "#ff6666",
  fontSize: "0.9rem",
  lineHeight: 1.5,
  textAlign: "left",
  marginBottom: 12,
},
noticeText: {
  color: "#bbb",
  fontSize: "0.9rem",
  marginBottom: 10,
},
glowBox: {
  padding: 20,
  borderRadius: 16,
  marginBottom: 16,
  background: "rgba(40, 0, 0, 0.5)",
  textAlign: "center",
},


};
